import { useEffect, useState } from "react";
import { motion } from "framer-motion";

import { DEMO_FLOW, DEMO_DECISION, DEMO_MOMENT } from "../../lib/demoContent";
import { SceneCaption } from "./SceneCaption";

const STEP_COLORS = {
  local: { bg: "transparent", border: "#DDD9CF", text: "#56534A", sub: "Local — free" },
  cacheable: { bg: "#FBF1DC", border: "#E8A930", text: "#7A5A12", sub: "Cacheable" },
  api: { bg: "#FBE4D8", border: "#C4521E", text: "#7A2E0C", sub: "API required" },
};

/** Number counter — climbs from 0 → target over `duration` ms. Eases out cubic. */
const Counter = ({ target, duration = 1500, prefix = "", suffix = "" }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setVal(Math.round(target * ease(t)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return (
    <span className="tabular-nums">
      {prefix}
      {val.toLocaleString()}
      {suffix}
    </span>
  );
};

/** Scene 5 — THE MOMENT. Flow steps reveal in sequence, decision banner slides in,
 *  then the "What matters here" strip lands with the value floor counting up. */
export const ScreenFlow = () => (
  <div data-testid="demo-scene-moment" className="max-w-5xl">
    <SceneCaption eyebrow="The picture" body="Then it showed her the picture." />

    {/* Flow diagram — steps reveal left-to-right */}
    <div className="mt-6 text-[10px] uppercase tracking-[0.20em] text-[#56534A] mb-3">
      The feature flow
    </div>

    <div className="flex items-stretch gap-1.5 xl:gap-2 pb-2 overflow-x-auto md:overflow-visible">
      {DEMO_FLOW.map((s, i) => {
        const c = STEP_COLORS[s.type];
        return (
          <motion.div
            key={s.step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 + i * 0.5 }}
            className="flex items-stretch gap-1.5 xl:gap-2 flex-1 min-w-0"
          >
            <div
              className="rounded-md px-2.5 py-3 xl:px-3 xl:py-3.5 flex-1 min-w-0 flex flex-col"
              style={{
                backgroundColor: c.bg,
                border: `1px solid ${c.border}`,
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: c.text }}>
                Step {s.step}
              </div>
              <div className="font-serif-display text-sm xl:text-base text-[#1A1814] mt-1 leading-tight">
                {s.label}
              </div>
              <div className="text-[10px] xl:text-[11px] mt-auto pt-3" style={{ color: c.text }}>
                {c.sub}
              </div>
            </div>
            {i < DEMO_FLOW.length - 1 && (
              <span className="text-[#DDD9CF] text-sm shrink-0 self-center">→</span>
            )}
          </motion.div>
        );
      })}
    </div>

    {/* Decision banner — slides in after flow lays out */}
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 4.6 }}
      className="mt-6 border border-[#C4521E] rounded-md p-5 bg-[#FBE4D8]/40 flex gap-5"
    >
      <div className="text-xs uppercase tracking-[0.18em] text-[#C4521E] font-medium shrink-0 mt-1">
        Decision
      </div>
      <div className="text-[#1A1814] leading-relaxed">
        <span className="font-medium">{DEMO_DECISION.headline} </span>
        <span>{DEMO_DECISION.body}</span>
      </div>
    </motion.div>

    {/* THE STRIP — the cinematic beat. Slides in, then the floor number climbs. */}
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 6.0, ease: [0.22, 1, 0.36, 1] }}
      className="mt-5 border border-[#DDD9CF] bg-white rounded-md p-6 xl:p-7"
    >
      <div className="text-[10px] uppercase tracking-[0.20em] text-[#C4521E]">
        What matters here
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 7.0 }}
        className="mt-4 text-xl xl:text-2xl text-[#1A1814] font-serif-display leading-relaxed"
      >
        For this to be worth building, it must create more than{" "}
        <span className="text-[#C4521E] font-medium">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 7.4 }}
            className="inline-block"
          >
            <CounterAt delayMs={7400} target={DEMO_MOMENT.value_floor_usd} prefix="$" suffix="/month" />
          </motion.span>
        </span>
        .
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 9.4 }}
        className="mt-2 text-sm text-[#56534A]"
      >
        That's{" "}
        <CounterAt
          delayMs={9600}
          target={DEMO_MOMENT.annual_floor_usd}
          prefix="$"
          suffix="/year"
        />
        .
      </motion.p>

      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 11.5 }}
        className="mt-6 text-base text-[#1A1814] leading-relaxed border-t border-[#DDD9CF] pt-5"
      >
        Operating cost is dominated by{" "}
        <span className="font-medium">human review</span> and{" "}
        <span className="font-medium">wrong-output exposure</span> —
        ${DEMO_MOMENT.review_usd.toLocaleString()}/mo + $
        {DEMO_MOMENT.error_usd.toLocaleString()}/mo combined.
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 13.0 }}
        className="mt-2 text-sm text-[#C4521E] font-medium"
      >
        Token cost alone is {DEMO_MOMENT.token_share_pct}% of the real number.
      </motion.p>
    </motion.div>
  </div>
);

/** Counter that waits `delayMs` then climbs. */
const CounterAt = ({ delayMs, target, prefix = "", suffix = "" }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);
  if (!ready) {
    return (
      <span className="tabular-nums">
        {prefix}0{suffix}
      </span>
    );
  }
  return <Counter target={target} duration={1500} prefix={prefix} suffix={suffix} />;
};
